import { useQuery } from '@tanstack/react-query';
import { listDomains, domainsKeys, type DomainPack } from './api/domains.gen';
import { useAuth } from './auth';

// Vocabulary layer (P03 §5, story 3-2).
//
// WHAT IS AND IS NOT VOCABULARISED — the decision this whole file rests on
// (P03 §0, the 2026-08 grooming decision):
//
//   * The global registry is **Sites**. That name is a CONSTANT. The nav row,
//     the breadcrumb and the list title say "Sites" under every domain pack,
//     and none of them may call vocab().
//   * A **record** inside that registry uses its pack's noun — Building,
//     Website, Machine, Outdoor site. Cards, forms, filters, buttons, empty
//     states, report headings and search results resolve through `vocab()`.
//
// Getting that backwards ("Buildings" in the nav) is the failure mode this
// comment exists to prevent.
//
// Resolution order for a term (P03 §5: "…with building defaults"):
//   1. the requested pack's own string;
//   2. the default (building) pack's string — so a pack that omits a term
//      inherits rather than showing a raw key;
//   3. a neutral fallback used ONLY before the registry has loaded: the
//      registry noun for Subject/Subjects, the term itself for everything else.
//      Deliberately NOT the building nouns — a pre-hydration render must not
//      assert a domain it hasn't read yet, or a website org sees "Building"
//      flash before "Website".

export type VocabTerm = 'Subject' | 'Subjects' | 'Unit' | 'Units' | 'Finding' | 'Findings' | 'Walkthrough';

// Step 3 above. `Subject`/`Subjects` fall back to the registry's own constant
// noun; every other term is already an ordinary English word.
const NEUTRAL_FALLBACK: Partial<Record<VocabTerm, string>> = {
  Subject: 'Site',
  Subjects: 'Sites',
};

// The CONSTANT registry name (P03 §0/§5). Exported so the shell and list title
// read it from one place and a future reader can see it is intentionally not a
// vocab term.
export const SITES_REGISTRY_LABEL = 'Sites';
export const SITES_REGISTRY_LABEL_SINGULAR = 'Site';

export interface VocabRegistry {
  packs: DomainPack[];
  defaultDomainKey: string;
}

function packStrings(pack: DomainPack | undefined, term: VocabTerm): string | undefined {
  if (!pack) return undefined;
  // Record nouns live on subjectSchema.labels — one source per string, so a
  // pack never declares its own name twice.
  if (term === 'Subject') return pack.subjectSchema.labels.singular;
  if (term === 'Subjects') return pack.subjectSchema.labels.plural;
  return pack.vocabulary[term];
}

/**
 * Pure term resolution — exported so it can be exercised without a query
 * client (the /app/design gallery renders the same component under two packs
 * through this exact function).
 */
export function resolveVocab(registry: VocabRegistry, term: VocabTerm, domainKey?: string): string {
  const byKey = (key: string | undefined): DomainPack | undefined =>
    key ? registry.packs.find((pack) => pack.key === key) : undefined;
  return (
    packStrings(byKey(domainKey), term) ??
    packStrings(byKey(registry.defaultDomainKey), term) ??
    NEUTRAL_FALLBACK[term] ??
    term
  );
}

/**
 * The pack whose nouns an unqualified `vocab('Subject')` resolves to: the org's
 * single active pack when there is exactly one, otherwise the default pack.
 *
 * With more than one pack active, P03 §5 puts a `DomainSwitcher` in the top bar
 * to choose between them; that control is NOT this story (the story's scope
 * note keeps activation UX out), so the unqualified resolution stays on the
 * default pack until it lands. Nothing is blocked by that: every record carries
 * its own `domainKey`, so per-record naming — the thing the story actually
 * requires — is always exact via `vocab('Subject', record.domainKey)`.
 */
export function resolveActiveDomainKey(registry: VocabRegistry): string {
  const active = registry.packs.filter((pack) => pack.active);
  const only = active.length === 1 ? active[0] : undefined;
  return only ? only.key : registry.defaultDomainKey;
}

export interface VocabValue {
  /** Resolve a term, optionally for one specific pack (a record's domainKey). */
  vocab: (term: VocabTerm, domainKey?: string) => string;
  /** Every seeded pack, active or not — a record's noun must resolve either way. */
  packs: DomainPack[];
  /** Packs this org has activated (P03 §3.1) — what a domain picker offers. */
  activePacks: DomainPack[];
  activeDomainKey: string;
  defaultDomainKey: string;
  isHydrated: boolean;
}

/**
 * `useVocab()` — the hook P03 §5 specifies. Reads the domain registry through
 * the generated client (contract-first; no hand-rolled fetch) and its query-key
 * factory (G-19). The registry is small, platform-owned and changes only on a
 * deploy or a flag flip, so it is cached for the session and refetched on the
 * same governance signal that refetches capabilities (a flag flip changes
 * `active`, and the capability refetch invalidates nothing else here).
 */
export function useVocab(): VocabValue {
  const { token } = useAuth();
  const { data, isSuccess } = useQuery({
    queryKey: domainsKeys.list(),
    queryFn: listDomains,
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
  });

  const registry: VocabRegistry = {
    packs: data?.data ?? [],
    defaultDomainKey: data?.defaultDomainKey ?? 'building',
  };

  return {
    vocab: (term, domainKey) => resolveVocab(registry, term, domainKey ?? resolveActiveDomainKey(registry)),
    packs: registry.packs,
    activePacks: registry.packs.filter((pack) => pack.active),
    activeDomainKey: resolveActiveDomainKey(registry),
    defaultDomainKey: registry.defaultDomainKey,
    isHydrated: isSuccess,
  };
}
